import {getCookie} from '@optimaxdev/utils';
import {createAction, createReducer} from '@reduxjs/toolkit';

import {ENDPOINTS} from 'constants/endpoints';
import {apiConfig, HTTP_REQUEST_HEADER} from 'libs/api/api';

import {getExtraUrlData} from './utils';

type SubscribeArgumentsType = {
    from: string;
    affId: string;
    verifyRealEmail: boolean;
    subscriptionMethod?: 'Auto' | 'Manual';
    state?: string;
    country?: string;
};

export const initialState = {
    emailSubscription: {
        isCheckboxVisible: false,
        isChecked: true,
    },
    smsSubscription: {
        isCheckboxVisible: false,
        isChecked: false,
    },
};
export const setEmailCheckboxVisibility = createAction<boolean>(
    'CHECKOUT@SET_EMAIL_CHECKBOX_VISIBILITY',
);

export const setEmailCheckboxStatus = createAction<boolean>('CHECKOUT@SET_EMAIL_CHECKBOX_STATUS');

export const setSmsCheckboxStatus = createAction<boolean>('CHECKOUT@SET_SMS_CHECKBOX_STATUS');

export const setSmsCheckboxBeenShowed = createAction<boolean>(
    'CHECKOUT@SET_SMS_CHECKBOX_VISIBILITY',
);

/**
 * Subscribe a customer
 * THIS ACTION IS ONLY FOR DISPATCHING TO HOST
 * @param {string} email Email for subscription
 * @param {{from: string, affId: string, verifyRealEmail: boolean}} parameters - Additional arguments
 * @returns {ActionType} Action
 */
export function subscribe(
    email: string,
    {
        affId = '',
        country = '',
        from = '',
        state = '',
        subscriptionMethod = 'Manual',
        verifyRealEmail = false,
    }: SubscribeArgumentsType,
) {
    const {referral, ...extraUrlCookie} = getExtraUrlData();
    const remoteIp = getCookie('realIp') || '';
    const timezone = new Date().getTimezoneOffset().toString() || '';

    const shouldVerifyEmail = process.env.APP_ENV === 'production' && verifyRealEmail;

    const data = {
        affid: affId,
        client: {
            client_country_name: country,
            client_region_name: state,
            remoteIp,
            timezone,
            user_agent: window?.navigator?.userAgent || '',
        },
        email,
        ...(referral ? {referral} : {}),
        subscribed_from: from,
        subscription_method: subscriptionMethod,
        utmData: {
            ...extraUrlCookie,
        },
        verify_real_email: shouldVerifyEmail,
    };

    return {
        meta: {
            email,
            from,
        },
        payload: apiConfig.instanceGusaBackend({
            baseURL: apiConfig.API_URL,
            data,
            headers: {
                [HTTP_REQUEST_HEADER.IS_REACT]: '1',
            },
            method: 'post',
            url: `${ENDPOINTS.SUBSCRIBE}`,
        }),
        type: 'NEWSLETTER@SUBSCRIBE',
    };
}

/**
 * Deactivate a customer
 * THIS ACTION IS ONLY FOR DISPATCHING TO HOST
 * @param {string} email Email for deactivate
 * @returns {ActionType} Action
 */
export function deactivate(email: string) {
    return {
        payload: apiConfig.instanceGusaBackend({
            baseURL: apiConfig.API_URL,
            data: {
                destination: email,
                type: 'email',
            },
            headers: {
                [HTTP_REQUEST_HEADER.IS_REACT]: '1',
            },
            method: 'put',
            url: ENDPOINTS.DEACTIVATE,
        }),
        type: 'NEWSLETTER@DEACTIVATE',
    };
}

/**
 * mark a customer as transitional
 * THIS ACTION IS ONLY FOR DISPATCHING TO HOST
 * @param {string} email Email for mark customer as transitional
 * @returns {ActionType} Action
 */
export function createTransitional(email: string) {
    return {
        payload: apiConfig.instanceGusaBackend({
            baseURL: apiConfig.API_URL,
            data: {
                email,
            },
            headers: {
                [HTTP_REQUEST_HEADER.IS_REACT]: '1',
            },
            method: 'post',
            url: ENDPOINTS.CREATE_TRANSITIONAL,
        }),
        type: 'NEWSLETTER@CREATE_TRANSITIONAL',
    };
}

/**
 * newsletter subscription
 */
export const newsletterSubscription = createReducer(initialState, builder =>
    builder
        .addCase(setEmailCheckboxVisibility, (state, action) => ({
            ...state,
            emailSubscription: {
                ...state.emailSubscription,
                isCheckboxVisible: action.payload,
            },
        }))
        .addCase(setEmailCheckboxStatus, (state, action) => ({
            ...state,
            emailSubscription: {...state.emailSubscription, isChecked: action.payload},
        }))
        .addCase(setSmsCheckboxStatus, (state, action) => ({
            ...state,
            smsSubscription: {...state.smsSubscription, isChecked: action.payload},
        }))
        .addCase(setSmsCheckboxBeenShowed, (state, action) => ({
            ...state,
            smsSubscription: {...state.smsSubscription, isCheckboxVisible: action.payload},
        })),
);
